import React, { useState, useEffect } from 'react';
import OrderCard from './OrderCard';
import { supabase } from '../supabaseClient';

// MOCK DATA PARA DEMO O SI NO HAY BUSINESS ID
const INITIAL_ORDERS = [
  { id: '1042', customerName: 'Sergio Reina', timeElapsed: 2, status: 'new', items: ['2x Pizza Hawaiana Familiar', '1x Coca-Cola 2L'], notes: 'Sin piña en una mitad' },
  { id: '1043', customerName: 'María Gomez', timeElapsed: 1, status: 'new', items: ['1x Hamburguesa Doble', '1x Papas Fritas'], notes: 'Sin cebolla' },
  { id: '1039', customerName: 'Carlos López', timeElapsed: 15, status: 'preparing', items: ['3x Tacos al Pastor', '1x Agua de Horchata'], notes: '' },
  { id: '1035', customerName: 'Ana Martínez', timeElapsed: 25, status: 'ready', items: ['1x Ensalada César', '1x Jugo Verde'], notes: 'Aderezo aparte' },
];

const parseOrderItems = (order) => {
  if (Array.isArray(order.items)) {
    return order.items.map(i => typeof i === 'string' ? i : `${i.qty || 1}x ${i.name || i.title || 'Ítem'}`);
  }
  if (typeof order.items === 'string') {
    try {
      const parsed = JSON.parse(order.items);
      if (Array.isArray(parsed)) {
        return parsed.map(i => typeof i === 'string' ? i : `${i.qty || 1}x ${i.name || i.title || 'Ítem'}`);
      }
      return [order.items];
    } catch (e) {
      return [order.items];
    }
  }
  return ['Pedido especial'];
};

const normalizeOrder = (order) => {
  let status = order.status;
  if (status === 'pending') status = 'new';
  if (status === 'in_preparation') status = 'preparing';
  
  const created = order.created_at ? new Date(order.created_at).getTime() : Date.now();
  const timeElapsed = Math.max(0, Math.floor((Date.now() - created) / 60000));

  return {
    ...order,
    id: String(order.id),
    rawId: order.id,
    customerName: order.client_name || order.customer_name || order.customerName || 'Cliente',
    timeElapsed: timeElapsed || order.timeElapsed || 0,
    status: status || 'new',
    items: parseOrderItems(order),
    notes: order.notes || order.delivery_address || order.direccion_entrega || order.customer_address || ''
  };
};

const KitchenMonitor = () => {
  const [orders, setOrders] = useState(INITIAL_ORDERS);
  const [businessId, setBusinessId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('id');
    if (id) {
      setBusinessId(id);
      fetchLiveOrders(id);
      const interval = setInterval(() => fetchLiveOrders(id), 5000);
      return () => clearInterval(interval);
    }
  }, []);

  const fetchLiveOrders = async (id) => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('business_id', id)
        .in('status', ['pending', 'in_preparation', 'ready']);

      if (error) throw error;
      if (data && data.length > 0) {
        setOrders(data.map(normalizeOrder));
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.warn("Error cargando pedidos en cocina:", err.message);
    }
  };

  const handleMoveOrder = async (orderId, newStatus) => {
    setOrders(prev => prev.map(order => 
      order.id === orderId ? { ...order, status: newStatus } : order
    ));

    if (businessId) {
      let dbStatus = newStatus;
      if (newStatus === 'new') dbStatus = 'pending';
      if (newStatus === 'preparing') dbStatus = 'in_preparation';
      if (newStatus === 'ready') dbStatus = 'ready';
      if (newStatus === 'delivered') dbStatus = 'shipped';

      try {
        const order = orders.find(o => o.id === orderId);
        const targetId = order?.rawId || orderId;
        await supabase
          .from('orders')
          .update({ status: dbStatus })
          .eq('id', targetId);
      } catch (err) {
        console.warn("Error actualizando pedido en BD:", err.message);
      }
    }
  };

  const getOrdersByStatus = (status) => orders.filter(o => o.status === status);

  return (
    <div className="min-h-screen bg-[#0f172a] text-white p-6 font-sans selection:bg-pink-500 selection:text-white">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-orange-400 to-pink-500">
            Monitor de Cocina 🍳
          </h1>
          <p className="text-gray-400 mt-1">Keyla - Modo Restaurante</p>
        </div>
        <div className="flex gap-4">
          <div className="bg-[#1e293b] px-4 py-2 rounded-lg border border-gray-700 flex items-center gap-2 shadow-lg">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
            <span className="text-sm font-medium">Recibiendo Pedidos en Vivo</span>
          </div>
        </div>
      </div>

      {/* Tablero Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[calc(100vh-140px)]">
        
        {/* Columna: Nuevos */}
        <div className="bg-[#1e293b] bg-opacity-50 rounded-2xl border border-gray-800 p-4 flex flex-col shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-800">
            <h2 className="text-lg font-bold text-orange-400 flex items-center gap-2">
              <span className="text-2xl">🔔</span> Nuevos
            </h2>
            <span className="bg-orange-500/20 text-orange-400 px-3 py-1 rounded-full text-sm font-bold">
              {getOrdersByStatus('new').length}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-4">
            {getOrdersByStatus('new').map(order => (
              <OrderCard key={order.id} order={order} onMove={handleMoveOrder} />
            ))}
            {getOrdersByStatus('new').length === 0 && (
              <div className="text-center text-gray-500 mt-10 italic">Esperando pedidos...</div>
            )}
          </div>
        </div>

        {/* Columna: Preparando */}
        <div className="bg-[#1e293b] bg-opacity-50 rounded-2xl border border-gray-800 p-4 flex flex-col shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-800">
            <h2 className="text-lg font-bold text-yellow-400 flex items-center gap-2">
              <span className="text-2xl">👨‍🍳</span> Preparando
            </h2>
            <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm font-bold">
              {getOrdersByStatus('preparing').length}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-4">
            {getOrdersByStatus('preparing').map(order => (
              <OrderCard key={order.id} order={order} onMove={handleMoveOrder} />
            ))}
          </div>
        </div>

        {/* Columna: Listos */}
        <div className="bg-[#1e293b] bg-opacity-50 rounded-2xl border border-gray-800 p-4 flex flex-col shadow-2xl overflow-hidden">
          <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-800">
            <h2 className="text-lg font-bold text-green-400 flex items-center gap-2">
              <span className="text-2xl">✅</span> Listos / Despacho
            </h2>
            <span className="bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-bold">
              {getOrdersByStatus('ready').length}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-4">
            {getOrdersByStatus('ready').map(order => (
              <OrderCard key={order.id} order={order} onMove={handleMoveOrder} />
            ))}
          </div>
        </div>

      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(30, 41, 59, 0.5);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(71, 85, 105, 0.8);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(100, 116, 139, 1);
        }
      `}</style>
    </div>
  );
};

export default KitchenMonitor;
